import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link as LinkIcon, Copy, Check, Plus, Trash2, Power, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";

function LinkRow({ item, isDefault, onCopy, onEdit, onDelete, onToggle }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullUrl = `${origin}/apply/${item.code}`;
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  const disabled = item.active === false;
  return (
    <div
      className={`py-4 border-t border-slate-200 ${disabled ? "opacity-50" : ""}`}
      data-testid={`referral-link-row-${item.id}`}
    >
      <div className="grid grid-cols-12 gap-4 items-baseline">
        <div className="col-span-12 md:col-span-6">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-[16px] text-[#1F2937]" data-testid={`referral-link-label-${item.id}`}>
              {item.label}
            </span>
            {isDefault && <span className="small-caps text-slate-400 text-[10px]">Primary</span>}
            {disabled && <span className="small-caps text-slate-500 text-[10px]">Disabled</span>}
          </div>
          <div className="mt-1 font-mono text-[12px] text-slate-700 break-all" data-testid={`referral-link-url-${item.id}`}>
            {fullUrl}
          </div>
          <div className="mt-1 small-caps text-slate-500 text-[10px]">
            Code <span className="font-mono text-[#1F2937]">{item.code}</span>
          </div>
        </div>
        <div className="col-span-6 md:col-span-2 flex md:justify-center gap-6">
          <div className="text-right md:text-center">
            <div className="small-caps text-slate-400 text-[10px]">Visits</div>
            <div className="font-mono text-[14px] text-[#1F2937]" data-testid={`referral-link-visits-${item.id}`}>
              {item.visits ?? 0}
            </div>
          </div>
          <div className="text-right md:text-center">
            <div className="small-caps text-slate-400 text-[10px]">Signups</div>
            <div className="font-mono text-[14px] text-[#1F2937]" data-testid={`referral-link-signups-${item.id}`}>
              {item.signups ?? 0}
            </div>
          </div>
        </div>
        <div className="col-span-6 md:col-span-4 flex items-center justify-end gap-4 flex-wrap">
          <button
            onClick={doCopy}
            className="inline-flex items-center gap-1.5 small-caps text-[#1F2937] hover:opacity-70"
            data-testid={`referral-link-copy-${item.id}`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
          {!isDefault && (
            <>
              <button
                onClick={() => onEdit(item)}
                className="inline-flex items-center gap-1.5 small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid={`referral-link-edit-${item.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onToggle(item)}
                className="inline-flex items-center gap-1.5 small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid={`referral-link-toggle-${item.id}`}
                title={disabled ? "Enable" : "Disable"}
              >
                <Power className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onDelete(item)}
                className="inline-flex items-center gap-1.5 small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid={`referral-link-delete-${item.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReferralLinkCard({ role }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null); // {id, label}

  const load = () =>
    api
      .get("/referral-links")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const all = [data.default, ...(data.links || [])];

  const createLink = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      await api.post("/referral-links", { label });
      setNewLabel(""); setAdding(false);
      toast.success("Link created");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    if (!editing?.id || !editing.label?.trim()) return;
    try {
      await api.patch(`/referral-links/${editing.id}`, { label: editing.label.trim() });
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleLink = async (item) => {
    try {
      await api.patch(`/referral-links/${item.id}`, { active: !item.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteLink = async (item) => {
    if (!confirm(`Delete link "${item.label}"? Signups already attributed stay attributed.`)) return;
    try {
      await api.delete(`/referral-links/${item.id}`);
      toast.success("Link removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const label = role === "loan_rep" ? "Your officer application links" : "Your broker application links";
  const sub =
    role === "loan_rep"
      ? "Anyone who applies through these links is routed to you automatically."
      : "Clients who apply through your links are attributed to your book. Create separate links per channel to track sources.";

  return (
    <section className="border border-slate-200 bg-white p-6" data-testid="referral-link-card">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <LinkIcon className="h-4 w-4 text-[#1F2937]" />
          <div>
            <div className="small-caps text-slate-500">Application links</div>
            <h3 className="mt-1 font-semibold text-[22px] text-[#1F2937]">{label}</h3>
          </div>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 small-caps text-[#1F2937] hover:opacity-70"
          data-testid="referral-link-new-btn"
        >
          <Plus className="h-3.5 w-3.5" /> New link
        </button>
      </div>
      <p className="mt-3 text-[13.5px] text-slate-600 max-w-2xl">{sub}</p>

      {adding && (
        <div className="mt-6 flex items-end gap-4 border-t border-slate-200 pt-4" data-testid="referral-link-new-form">
          <div className="flex-1">
            <div className="small-caps text-slate-500 mb-1">Label (visible only to you)</div>
            <Input
              autoFocus
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createLink()}
              placeholder="e.g., LinkedIn campaign, Email blast, Trade show…"
              data-testid="referral-link-new-input"
              className="border-0 border-b border-slate-400 rounded-none px-0 bg-transparent shadow-none font-semibold text-[15px] focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <button
            onClick={createLink}
            disabled={!newLabel.trim()}
            className="small-caps text-[#1F2937] disabled:opacity-40"
            data-testid="referral-link-new-save"
          >
            Save
          </button>
          <button
            onClick={() => { setAdding(false); setNewLabel(""); }}
            className="small-caps text-slate-500 hover:text-[#1F2937]"
            data-testid="referral-link-new-cancel"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="mt-4" data-testid="referral-link-list">
        {all.map((item) => {
          const isDefault = item.id === "default";
          if (editing?.id === item.id) {
            return (
              <div key={item.id} className="py-4 border-t border-slate-200 flex items-end gap-4" data-testid={`referral-link-edit-form-${item.id}`}>
                <div className="flex-1">
                  <div className="small-caps text-slate-500 mb-1">Rename</div>
                  <Input
                    autoFocus
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                    data-testid={`referral-link-edit-input-${item.id}`}
                    className="border-0 border-b border-slate-400 rounded-none px-0 bg-transparent shadow-none font-semibold text-[15px] focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
                <button onClick={saveEdit} className="small-caps text-[#1F2937]" data-testid={`referral-link-edit-save-${item.id}`}>Save</button>
                <button onClick={() => setEditing(null)} className="small-caps text-slate-500" data-testid={`referral-link-edit-cancel-${item.id}`}>Cancel</button>
              </div>
            );
          }
          return (
            <LinkRow
              key={item.id}
              item={item}
              isDefault={isDefault}
              onCopy={() => toast.success("Link copied")}
              onEdit={(it) => setEditing({ id: it.id, label: it.label })}
              onDelete={deleteLink}
              onToggle={toggleLink}
            />
          );
        })}
      </div>
    </section>
  );
}
